import React, { useCallback, useRef, useState } from 'react';
import { FaArrowLeft, FaCheckCircle, FaUpload } from 'react-icons/fa';
import {
  commitVendorBulkUpdate,
  validateVendorBulkUpdate,
} from '../../services/shipturtleApi';
import { showToast } from '../../utils/toast';
import './ManageVendors.css';
import VendorDiffPreviewTable from './VendorDiffPreviewTable';
import styles from './BulkUpdateVendorsPage.module.css';

const STEPS = [
  { key: 'upload', label: 'Upload' },
  { key: 'preview', label: 'Preview' },
  { key: 'complete', label: 'Done' },
];

const ACCEPTED = '.xlsx,.xls,.csv';

/**
 * Vendor bulk update: upload a sheet, review exactly what will change, apply it.
 *
 * Update-only by design — there is no create path here. Rows are matched on the
 * vendor_id column carried by the export, so a row can only ever land on the vendor
 * it came from.
 */
const BulkUpdateVendorsPage = ({ onBack }) => {
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const reset = useCallback(() => {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleValidate = useCallback(async (selected) => {
    setIsBusy(true);
    setError(null);
    try {
      const response = await validateVendorBulkUpdate(selected);
      setPreview(response);
      setStep('preview');
    } catch (err) {
      setError(err.message || 'Could not read that file.');
    } finally {
      setIsBusy(false);
    }
  }, []);

  const handleFileSelect = useCallback(
    (event) => {
      const selected = event.target.files?.[0];
      if (!selected) return;
      setFile(selected);
      handleValidate(selected);
    },
    [handleValidate],
  );

  const handleApply = useCallback(async () => {
    if (!preview) return;
    setIsBusy(true);
    setError(null);
    try {
      // Only rows that actually differ are sent; unchanged and failed rows have
      // nothing to write and the backend would skip them anyway.
      const rows = preview.results.filter((row) => row.changed).map((row) => row.data);
      const response = await commitVendorBulkUpdate(rows);
      setResult(response);
      setStep('complete');
      showToast({
        type: 'success',
        message: `Updated ${response.updated} vendor${response.updated === 1 ? '' : 's'}.`,
      });
    } catch (err) {
      setError(err.message || 'The update could not be applied.');
    } finally {
      setIsBusy(false);
    }
  }, [preview]);

  const changedCount = preview?.summary?.valid_count || 0;
  const errorCount = preview?.summary?.error_count || 0;

  return (
    <div className="manageVendorsContainer">
      <div className="vendorsWhiteBox">
      <div className={styles.header}>
        <button type="button" className={styles.backBtn} onClick={onBack}>
          <FaArrowLeft /> Back to vendors
        </button>
        <h2>Import vendor updates</h2>
      </div>

      <ol className={styles.steps}>
        {STEPS.map((entry, index) => {
          const activeIndex = STEPS.findIndex((s) => s.key === step);
          const state =
            index < activeIndex ? styles.stepDone : index === activeIndex ? styles.stepActive : '';
          return (
            <li key={entry.key} className={`${styles.step} ${state}`}>
              <span className={styles.stepNum}>{index + 1}</span>
              {entry.label}
            </li>
          );
        })}
      </ol>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {step === 'upload' && (
        <div className={styles.card}>
          <p className={styles.lead}>
            Update existing vendors from a spreadsheet. This screen never creates new
            vendors.
          </p>
          <ul className={styles.rules}>
            <li>Only rows whose values you change are updated.</li>
            <li>Blank cells are left alone — they never clear an existing value.</li>
            <li>
              Do not edit or remove the <code>vendor_id</code> column; it is how each row
              is matched.
            </li>
          </ul>
          <p className={styles.hint}>
            Need a sheet? Select vendors on the Manage Vendors list and use{' '}
            <strong>Export</strong> — that file is already in the right shape.
          </p>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy}
            >
              <FaUpload /> {isBusy ? 'Checking…' : 'Choose a file'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED}
              onChange={handleFileSelect}
              className={styles.hiddenInput}
            />
          </div>
          {file && <p className={styles.fileName}>{file.name}</p>}
        </div>
      )}

      {step === 'preview' && preview && (
        <div className={styles.card}>
          <VendorDiffPreviewTable summary={preview.summary} results={preview.results} />

          {preview.results_truncated && (
            <p className={styles.note}>
              Showing the first {preview.results.length} rows; the counts above cover the
              whole file.
            </p>
          )}

          <div className={styles.actions}>
            <button type="button" className={styles.secondaryBtn} onClick={reset}>
              Choose another file
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleApply}
              disabled={isBusy || !changedCount}
            >
              {isBusy
                ? `Updating ${changedCount} vendors…`
                : `Update ${changedCount} vendor${changedCount === 1 ? '' : 's'}`}
            </button>
          </div>
          {!changedCount && (
            <p className={styles.note}>
              Nothing in this sheet differs from what is already saved.
            </p>
          )}
          {!!errorCount && (
            <p className={styles.note}>
              {errorCount} row{errorCount === 1 ? '' : 's'} will be skipped because of the
              errors above. Fix them in the sheet and re-upload if they matter.
            </p>
          )}
        </div>
      )}

      {step === 'complete' && result && (
        <div className={styles.card}>
          <div className={styles.successHeader}>
            <FaCheckCircle className={styles.successIcon} />
            <div>
              <h3>Update applied</h3>
              <p className={styles.lead}>
                {result.updated} updated · {result.skipped_unchanged} unchanged ·{' '}
                {result.failed} failed
              </p>
            </div>
          </div>

          <VendorDiffPreviewTable
            summary={{
              valid_count: result.updated,
              unchanged_count: result.skipped_unchanged,
              error_count: result.failed,
              total_rows: result.results.length,
              changed_field_counts: result.changed_field_counts,
            }}
            results={result.results.map((row) => ({
              ...row,
              changed: row.status === 'updated',
              status: row.status === 'updated' ? 'valid' : row.status,
              warnings: [],
            }))}
          />

          <div className={styles.actions}>
            <button type="button" className={styles.secondaryBtn} onClick={reset}>
              Update another sheet
            </button>
            <button type="button" className={styles.primaryBtn} onClick={onBack}>
              Back to vendors
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default BulkUpdateVendorsPage;
